import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { clsx } from "clsx";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Badge } from "@/components/ui/Badge";
import { useCanSeeProcessGraph, useGraphNodeState, useGraphProposals, useProcessGraph } from "../advisor/hooks";
import {
  useAddMilestone,
  useDeleteMilestone,
  useReorderMilestones,
  useResetMilestoneLayout,
  useSetMilestone,
} from "../hooks";
import type { Milestone, MilestoneState, PowerProject } from "../types";
import { fmtDate } from "../utils";
import {
  Progress,
  SectionCard,
  milestoneMeta,
  MILESTONE_NA_STYLE,
  MILESTONE_STATES,
  MILESTONE_STATE_RANK,
} from "./ui";
import { MilestoneDetailModal } from "./MilestoneDetailModal";
import { MilestoneKanban } from "./MilestoneKanban";
import {
  MilestoneCounts,
  MilestoneFlagMarks,
  MilestoneProposalMark,
  MilestoneWodyPendingMark,
  MilestoneWodyStaleMark,
  wodyPendingFor,
  wodyStaleFor,
  type MilestoneFlags,
  type MilestoneProposal,
} from "./MilestoneCounts";
import { FactProposalsBanner } from "./FactProposalsBanner";

// Remembers the user's last-used milestone layout across projects / reloads.
const VIEW_STORAGE_KEY = "elg5:pp:milestones-view";
type MilestoneView = "grid" | "kanban";

export function MilestonesTab({ connection }: { connection: PowerProject }) {
  const setMilestone = useSetMilestone(connection.id);
  const reorder = useReorderMilestones(connection.id);
  const resetLayout = useResetMilestoneLayout(connection.id);
  const addMilestone = useAddMilestone(connection.id);
  const deleteMilestone = useDeleteMilestone(connection.id);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  // Layout: sortable grid (owns the global order) vs. status kanban (drag = change state).
  const [view, setView] = useState<MilestoneView>(() =>
    (typeof window !== "undefined" && window.localStorage.getItem(VIEW_STORAGE_KEY)) === "kanban"
      ? "kanban"
      : "grid",
  );
  useEffect(() => {
    window.localStorage.setItem(VIEW_STORAGE_KEY, view);
  }, [view]);
  // Which card's details modal is open (keyed by milestone `key`, so it tracks the live
  // milestone through refetches rather than pinning a stale snapshot).
  const [detailKey, setDetailKey] = useState<string | null>(null);
  // Deep link z powiadomienia („oznaczono Cię w notatce kamienia"): ?milestone=<key>
  // otwiera od razu szczegóły tego kroku. Parametr zdejmujemy po otwarciu, żeby
  // zamknięcie modala nie wracało do niego przy każdym renderze.
  const [searchParams, setSearchParams] = useSearchParams();
  const deepKey = searchParams.get("milestone");
  useEffect(() => {
    if (!deepKey) return;
    setDetailKey(deepKey);
    const next = new URLSearchParams(searchParams);
    next.delete("milestone");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepKey]);
  // Status wybrany na karcie / przeciągnięciem, który wymaga POWODU (cofnięcie albo
  // domknięcie kroku przed poprzednikami). Zamiast cichej odmowy otwieramy szczegóły
  // kroku z tym statusem podstawionym — powód wpisuje się tam, gdzie widać historię.
  const [pendingState, setPendingState] = useState<MilestoneState | null>(null);
  // Inline "add card" form (label only — status is set on the card afterwards).
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState("");

  // Card order is keyed by milestone `key` (enum type for built-ins, UUID for customs;
  // unique per connection). Drags save PER USER (milestone_layout); users who never
  // dragged have no saved layout (null) and see the shared default order. We hold the
  // order locally so a drag reorders instantly; we re-sync only when the server's
  // default or the saved layout changes (after our own write lands, that's a no-op).
  const serverOrder = connection.milestones.map((m) => m.key);
  const serverKey = serverOrder.join(",");
  const savedKey = connection.milestone_layout ? connection.milestone_layout.join(",") : null;
  const [order, setOrder] = useState<string[]>(connection.milestone_layout ?? serverOrder);
  useEffect(() => {
    const effective = savedKey ?? serverKey;
    setOrder(effective ? effective.split(",") : []);
  }, [serverKey, savedKey]);

  // Close the (compact) status picker on Escape or a click outside it. No full-screen
  // backdrop — that blocked page scroll while a menu was open.
  useEffect(() => {
    if (!openMenu) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (!t.closest(`[data-ms-status="${openMenu}"]`)) setOpenMenu(null);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpenMenu(null);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [openMenu]);

  const sensors = useSensors(
    // A small distance threshold so clicking the handle (or status/note) never
    // triggers an accidental drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const byKey = new Map(connection.milestones.map((m) => [m.key, m]));

  // Ten sam graf, który zasila schemat blokowy — karty pokazują z niego ostrzeżenie
  // o kolejności i ścieżkę krytyczną. Zapytanie jest współdzielone z zakładką
  // Kontroli procesu (ten sam klucz cache), więc nic nie dociąga się dwa razy.
  const canSeeGraph = useCanSeeProcessGraph();
  const graphQ = useProcessGraph(canSeeGraph ? connection.id : undefined);
  const flagsByKey = new Map<string, MilestoneFlags>(
    (graphQ.data?.nodes ?? []).map((n) => [
      n.key,
      { violation: n.violation, missing: n.missing_prereqs, critical: n.is_critical },
    ]),
  );

  // Niebieskie propozycje z silnika faktów — ten sam endpoint i ten sam hook, co pasek
  // nad tablicą i schemat blokowy (cache dzielony, więc karta nie dociąga niczego
  // dodatkowo). Na karcie ląduje propozycja kroku o tym samym `key`.
  const proposalsQ = useGraphProposals(canSeeGraph ? connection.id : undefined);
  const proposalsByKey = new Map<string, MilestoneProposal>(
    (proposalsQ.data?.proposals ?? []).map((p) => [p.node, p]),
  );
  const applyProposal = useGraphNodeState(connection.id);
  const onApplyProposal = (p: MilestoneProposal) =>
    applyProposal.mutate({ node: p.node, state: p.proposed_state, reason: p.reason });

  /** Zmiana statusu z karty (siatka) albo przeciągnięciem (kanban). Cofnięcie wymaga
   *  powodu — tak samo jak z grafu procesu — więc zamiast strzelać do backendu po
   *  odmowę, otwieramy szczegóły kroku z podstawionym statusem. Zamknięcie poza
   *  kolejnością zna dopiero backend (potrzebuje DAG-u), więc tam wchodzimy przez
   *  `onError`: karta wraca na miejsce, a modal prosi o powód. */
  const changeState = (
    mkey: string,
    state: MilestoneState,
    opts?: { onError?: () => void },
  ) => {
    setOpenMenu(null);
    const m = byKey.get(mkey);
    if (!m || m.state === state) return;
    const askForReason = () => {
      opts?.onError?.();
      setPendingState(state);
      setDetailKey(mkey);
    };
    if (MILESTONE_STATE_RANK[state] < MILESTONE_STATE_RANK[m.state]) {
      askForReason();
      return;
    }
    setMilestone.mutate({ mkey, state }, { onError: askForReason });
  };

  const submitNew = () => {
    const label = newLabel.trim();
    if (!label) return;
    addMilestone.mutate(
      { label },
      {
        onSuccess: () => {
          setNewLabel("");
          setAdding(false);
        },
      },
    );
  };

  const removeMilestone = (m: Milestone) => {
    if (window.confirm(`Usunąć kamień milowy „${m.type_display}"?`)) {
      deleteMilestone.mutate(m.key);
    }
  };

  // ── render order (robust to in-flight refetches) ───────────────────────────
  // The open modal reads the live milestone from the current list (stays fresh across refetches).
  const detailMilestone = detailKey ? byKey.get(detailKey) ?? null : null;
  const missing = serverOrder.filter((k) => !order.includes(k)); // newly added cards
  const fullOrder = [...order.filter((k) => byKey.has(k)), ...missing];
  const ordered = fullOrder.map((k) => byKey.get(k)!).filter(Boolean);

  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = fullOrder.indexOf(active.id as string);
    const to = fullOrder.indexOf(over.id as string);
    if (from < 0 || to < 0) return;
    const next = arrayMove(fullOrder, from, to);
    setOrder(next);
    reorder.mutate(next);
  };

  // Reset button shows as soon as this user's order differs from the default — either
  // a layout already saved on the server, or a fresh local drag still in flight.
  const hasCustomLayout = savedKey !== null || fullOrder.join(",") !== serverKey;
  const restoreDefaultLayout = () => {
    setOrder(serverOrder);
    resetLayout.mutate();
  };

  // ── progress summary ───────────────────────────────────────────────────────
  const total = connection.milestones.length;
  const countOf = (s: MilestoneState) =>
    connection.milestones.filter((m) => m.state === s).length;
  const naCount = countOf("na");
  const doneCount = countOf("done");
  const applicable = Math.max(0, total - naCount); // "Nie dotyczy" doesn't count against you
  const pct = applicable ? Math.round((doneCount / applicable) * 100) : 0;
  const now = new Date();

  return (
    <SectionCard
      title="Kamienie milowe"
      action={
        <div className="flex items-center gap-3">
          <span className="section-label normal-case tracking-normal hidden lg:inline">
            {view === "kanban"
              ? "przeciągnij między kolumnami = status · klik = szczegóły"
              : "⠿ kolejność · badge = status · klik = szczegóły"}
          </span>
          {view === "grid" && hasCustomLayout && (
            <button
              type="button"
              onClick={restoreDefaultLayout}
              disabled={resetLayout.isPending}
              title="Przywróć domyślną kolejność kafelków"
              className="shrink-0 inline-flex items-center gap-1 px-2 py-1 text-xs font-mono font-bold uppercase tracking-wide border border-[var(--color-border-strong)] text-[var(--color-text-secondary)] hover:border-[var(--color-primary)] hover:text-[var(--color-primary)] transition-colors disabled:opacity-50"
            >
              ↺ domyślny układ
            </button>
          )}
          <div className="inline-flex shrink-0 border border-[var(--color-border-strong)]">
            {(["grid", "kanban"] as const).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setView(v)}
                aria-pressed={view === v}
                className={clsx(
                  "px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-wide transition-colors",
                  view === v
                    ? "bg-[var(--color-primary)] text-white"
                    : "text-[var(--color-text-secondary)] hover:text-[var(--color-primary)]",
                )}
              >
                {v === "grid" ? "Siatka" : "Kanban"}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setAdding((v) => !v)}
            className="shrink-0 inline-flex items-center gap-1 px-2 py-1 text-xs font-mono font-bold uppercase tracking-wide border border-[var(--color-border-strong)] text-[var(--color-text-secondary)] hover:border-[var(--color-primary)] hover:text-[var(--color-primary)] transition-colors"
          >
            + kamień
          </button>
        </div>
      }
    >
      {/* inline add form — label only; status / termin / notatka are set on the card after */}
      {adding && (
        <div className="mb-4 flex flex-wrap items-center gap-2 p-3 border border-dashed border-[var(--color-border-strong)] bg-[var(--color-bg-elevated)]">
          <input
            autoFocus
            type="text"
            maxLength={120}
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") submitNew();
              if (e.key === "Escape") {
                setAdding(false);
                setNewLabel("");
              }
            }}
            placeholder="Nazwa kamienia milowego, np. Decyzja środowiskowa"
            className="flex-1 min-w-[200px] bg-[var(--color-bg-surface)] border border-[var(--color-border)] px-2 py-1.5 text-sm font-mono text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:border-[var(--color-primary)] focus:outline-none"
          />
          <button
            type="button"
            disabled={!newLabel.trim() || addMilestone.isPending}
            onClick={submitNew}
            className="px-3 py-1.5 text-xs font-mono font-bold uppercase tracking-wide bg-[var(--color-primary)] text-white hover:opacity-90 disabled:opacity-50 transition-opacity"
          >
            Dodaj
          </button>
          <button
            type="button"
            onClick={() => {
              setAdding(false);
              setNewLabel("");
            }}
            className="px-3 py-1.5 text-xs font-mono uppercase tracking-wide text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors"
          >
            Anuluj
          </button>
        </div>
      )}

      {/* summary band — done ratio, progress bar, per-state tally */}
      <div className="mb-4 flex flex-wrap items-center gap-x-5 gap-y-3 pb-4 border-b border-[var(--color-border-muted)]">
        <div className="flex items-baseline gap-2">
          <span className="font-mono text-2xl font-bold leading-none text-[var(--color-text-primary)] tabular-nums">
            {doneCount}
            <span className="text-base text-[var(--color-text-muted)]">/{applicable}</span>
          </span>
          <span className="section-label">gotowe</span>
        </div>
        <div className="flex flex-1 items-center gap-2 min-w-[140px]">
          <Progress pct={pct} className="flex-1" />
          <span className="font-mono text-xs font-bold text-[var(--color-text-secondary)] tabular-nums">
            {pct}%
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {MILESTONE_STATES.map((s) => {
            const n = countOf(s);
            if (!n) return null;
            const sm = milestoneMeta(s);
            return (
              <Badge key={s} color={sm.color}>
                {sm.label} · {n}
              </Badge>
            );
          })}
        </div>
      </div>

      {/* niebieskie propozycje z faktów — ten sam pasek co na schemacie blokowym,
          bez toru działek (na tablicy kamieni nie ma bloczków działek do klikania) */}
      {canSeeGraph && (
        <FactProposalsBanner
          connectionId={connection.id}
          title="Wykryte fakty — proponowane zmiany stanów"
          showTrackProposals={false}
          className="mb-4"
        />
      )}

      {view === "kanban" ? (
        <MilestoneKanban
          milestones={connection.milestones}
          now={now}
          onSetState={changeState}
          onOpenDetails={(mkey) => setDetailKey(mkey)}
          onDelete={removeMilestone}
          flagsByKey={flagsByKey}
          proposalsByKey={proposalsByKey}
          onApplyProposal={onApplyProposal}
          applyingProposal={applyProposal.isPending}
          wodyPendingCount={connection.wody_pending_count}
          wodyAnalysisStale={connection.wody_analysis_stale}
        />
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={fullOrder} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
              {ordered.map((m) => (
                <MilestoneCard
                  key={m.key}
                  m={m}
                  now={now}
                  open={openMenu === m.id}
                  onToggle={() => setOpenMenu(openMenu === m.id ? null : m.id)}
                  onPick={(s) => changeState(m.key, s)}
                  onOpenDetails={() => setDetailKey(m.key)}
                  onDelete={m.is_custom ? () => removeMilestone(m) : undefined}
                  pending={setMilestone.isPending}
                  flags={flagsByKey.get(m.key)}
                  proposal={proposalsByKey.get(m.key)}
                  onApplyProposal={onApplyProposal}
                  applyingProposal={applyProposal.isPending}
                  wodyPending={wodyPendingFor(m.key, connection.wody_pending_count)}
                  wodyStale={wodyStaleFor(m.key, connection.wody_analysis_stale)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {detailMilestone && (
        <MilestoneDetailModal
          connection={connection}
          milestone={detailMilestone}
          initialState={pendingState ?? undefined}
          onClose={() => {
            setDetailKey(null);
            setPendingState(null);
          }}
        />
      )}
    </SectionCard>
  );
}

// ── single sortable card ───────────────────────────────────────────────────────
function MilestoneCard({
  m,
  now,
  open,
  onToggle,
  onPick,
  onOpenDetails,
  onDelete,
  pending,
  flags,
  proposal,
  onApplyProposal,
  applyingProposal,
  wodyPending,
  wodyStale,
}: {
  m: Milestone;
  now: Date;
  open: boolean;
  onToggle: () => void;
  onPick: (state: MilestoneState) => void;
  onOpenDetails: () => void;
  onDelete?: () => void;
  pending: boolean;
  flags?: MilestoneFlags;
  /** Propozycja z faktów dla TEGO kroku (jeśli jest) — klik ją stosuje. */
  proposal?: MilestoneProposal;
  onApplyProposal?: (p: MilestoneProposal) => void;
  applyingProposal?: boolean;
  /** Wody Polskie: ile zdarzeń czeka na zlecenie podwykonawcy. */
  wodyPending?: number;
  /** Wody Polskie: detekcja policzona na starszej wersji trasy. */
  wodyStale?: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: m.key });
  const meta = milestoneMeta(m.state);
  const na = m.state === "na";
  const overdue =
    !!m.due_date && m.state !== "done" && m.state !== "na" && new Date(m.due_date) < now;

  return (
    <div
      ref={setNodeRef}
      onClick={onOpenDetails}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        // Enter/Space open the details modal — but only from the card itself, never when an
        // inner control (drag handle, status, delete) has focus.
        if ((e.key === "Enter" || e.key === " ") && e.target === e.currentTarget) {
          e.preventDefault();
          onOpenDetails();
        }
      }}
      title="Otwórz szczegóły"
      className={clsx(
        "card flex flex-col p-3 gap-2 cursor-pointer hover:shadow-hard-red focus:outline-none focus-visible:ring-1 focus-visible:ring-[var(--color-primary)]",
        isDragging && "shadow-hard-red z-30 opacity-90",
      )}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        borderLeft: `3px solid ${na ? "#4b5563" : meta.accent}`,
        ...(na
          ? MILESTONE_NA_STYLE
          : {
              background: `linear-gradient(0deg, ${meta.tint}, ${meta.tint}), var(--color-bg-surface)`,
            }),
      }}
    >
      {/* title row: drag handle · status dot · type · (delete, custom only) */}
      <div className="flex items-center gap-1.5 min-w-0">
        <button
          type="button"
          aria-label="Przeciągnij, aby zmienić kolejność"
          title="Przeciągnij, aby zmienić kolejność"
          className="shrink-0 -ml-0.5 px-0.5 text-sm leading-none text-[var(--color-text-muted)] hover:text-[var(--color-primary)] cursor-grab active:cursor-grabbing touch-none focus:outline-none"
          onClick={(e) => e.stopPropagation()}
          {...attributes}
          {...listeners}
        >
          ⠿
        </button>
        <span className="h-2 w-2 shrink-0" style={{ background: meta.accent }} aria-hidden />
        <div
          className="font-mono text-[0.7rem] font-bold uppercase tracking-wide text-[var(--color-text-primary)] truncate"
          title={m.type_display}
        >
          {m.type_display}
        </div>
        {/* Jak na kanbanie: kamień z ustaleń oddziału podpisujemy źródłem, nie „własny". */}
        {m.care_arrangement ? (
          <span
            className="shrink-0 font-mono text-[8px] uppercase tracking-wide text-amber-700 dark:text-amber-400 border border-amber-500 px-1 leading-tight"
            title={`Kamień z ustaleń oddziału klienta (${m.care_source ?? "karta specyfiki"}) — zakładka „Opieka”`}
          >
            z ustaleń
          </span>
        ) : m.is_custom && (
          <span
            className="shrink-0 font-mono text-[8px] uppercase tracking-wide text-[var(--color-text-muted)] border border-[var(--color-border)] px-1 leading-tight"
            title="Kamień dodany ręcznie"
          >
            własny
          </span>
        )}
        <MilestoneFlagMarks flags={flags} />
        <MilestoneWodyPendingMark count={wodyPending} />
                <MilestoneWodyStaleMark stale={wodyStale} />
        <MilestoneProposalMark
          proposal={proposal}
          onApply={onApplyProposal}
          disabled={applyingProposal}
        />
        {onDelete && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            aria-label="Usuń kamień milowy"
            title="Usuń kamień milowy"
            className="ml-auto shrink-0 px-0.5 text-sm leading-none text-[var(--color-text-muted)] hover:text-[var(--color-primary)] focus:outline-none"
          >
            ×
          </button>
        )}
      </div>

      {/* status: compact popover */}
      <div className="relative inline-block self-start" data-ms-status={m.id}>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          className="group inline-flex items-center gap-1 cursor-pointer"
          title="Zmień status"
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <Badge color={meta.color}>{meta.label}</Badge>
          <span
            className="text-[9px] leading-none text-[var(--color-text-muted)] transition-transform group-hover:text-[var(--color-primary)]"
            style={{ transform: open ? "rotate(180deg)" : undefined }}
            aria-hidden
          >
            ▾
          </span>
        </button>
        {open && (
          <div
            role="listbox"
            className="absolute left-0 top-full z-30 mt-1 min-w-[170px] card bg-[var(--color-bg-surface)] shadow-hard-red p-1 flex flex-col gap-0.5"
          >
            {MILESTONE_STATES.map((s) => {
              const sm = milestoneMeta(s);
              const current = s === m.state;
              return (
                <button
                  key={s}
                  type="button"
                  role="option"
                  aria-selected={current}
                  disabled={pending}
                  onClick={(e) => {
                    e.stopPropagation();
                    onPick(s);
                  }}
                  className={clsx(
                    "flex items-center justify-between gap-2 px-2 py-1.5 text-left transition-colors hover:bg-[var(--color-bg-elevated)] disabled:opacity-50",
                    current && "bg-[var(--color-bg-elevated)]",
                  )}
                >
                  <Badge color={sm.color}>{sm.label}</Badge>
                  {current && (
                    <span className="text-[var(--color-primary)] text-xs font-bold">✓</span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* surfaced dates: completion for done items, otherwise the deadline */}
      {m.state === "done" && m.completed_at ? (
        <div className="flex items-center gap-1 font-mono text-[10px] text-emerald-700">
          <span aria-hidden>✓</span>
          <span>ukończono {fmtDate(m.completed_at)}</span>
        </div>
      ) : m.due_date ? (
        <div
          className={clsx(
            "font-mono text-[10px]",
            overdue ? "text-[var(--color-primary)] font-bold" : "text-[var(--color-text-muted)]",
          )}
        >
          termin: {fmtDate(m.due_date)}
          {overdue && " · po terminie"}
        </div>
      ) : null}

      {/* footer — details affordance (notes & files now live in the modal) */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onOpenDetails();
        }}
        title="Notatki i pliki"
        className="mt-auto flex w-full items-center justify-between gap-1 pt-2 border-t border-[var(--color-border-muted)] text-[11px] font-mono text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors"
      >
        <MilestoneCounts notes={m.note_count} files={m.file_count} />
        <span aria-hidden>szczegóły →</span>
      </button>
    </div>
  );
}
